/**
 * PassengerPicker — popover content for the PAX pill.
 *
 * Adults 1-9, children 0-9, per-child age 0-17 (matches the existing
 * search-plus passenger UX). Emits the canonical `Passengers` shape
 * including `childrenAges` when there are children.
 */

import { Minus, Plus } from 'lucide-react'
import type { Passengers } from '@/lib/search/types'

interface PassengerPickerProps {
  value: Passengers
  onChange: (next: Passengers) => void
}

const MIN_ADULTS = 1
const MAX_ADULTS = 9
const MIN_CHILDREN = 0
const MAX_CHILDREN = 9
const MIN_AGE = 0
const MAX_AGE = 17
const DEFAULT_CHILD_AGE = 8

export function PassengerPicker({ value, onChange }: PassengerPickerProps) {
  const setAdults = (next: number) => {
    onChange({ ...value, adults: clamp(next, MIN_ADULTS, MAX_ADULTS) })
  }

  const setChildren = (next: number) => {
    const count = clamp(next, MIN_CHILDREN, MAX_CHILDREN)
    const ages = padOrTrimAges(value.childrenAges, count)
    onChange({
      adults: value.adults,
      children: count,
      ...(ages ? { childrenAges: ages } : {}),
    })
  }

  const setChildAge = (index: number, age: number) => {
    const ages = [...(value.childrenAges ?? [])]
    while (ages.length < value.children) ages.push(DEFAULT_CHILD_AGE)
    ages[index] = clamp(age, MIN_AGE, MAX_AGE)
    onChange({ ...value, childrenAges: ages.slice(0, value.children) })
  }

  return (
    <div className="sc-picker" data-passenger-picker>
      <div className="sc-picker-title">Travelers</div>

      <div className="sc-picker-row">
        <div>
          <div>Adults</div>
          <div className="sub">12+ years</div>
        </div>
        <Stepper
          value={value.adults}
          min={MIN_ADULTS}
          max={MAX_ADULTS}
          onChange={setAdults}
          ariaLabel="Adults"
        />
      </div>

      <div className="sc-picker-row">
        <div>
          <div>Children</div>
          <div className="sub">Under 12</div>
        </div>
        <Stepper
          value={value.children}
          min={MIN_CHILDREN}
          max={MAX_CHILDREN}
          onChange={setChildren}
          ariaLabel="Children"
        />
      </div>

      {value.children > 0 ? (
        <div>
          <div className="sc-picker-title" style={{ marginBottom: 6 }}>
            Child ages
          </div>
          <div className="sc-age-grid">
            {Array.from({ length: value.children }, (_, i) => {
              const age = value.childrenAges?.[i] ?? DEFAULT_CHILD_AGE
              return (
                <select
                  key={i}
                  value={age}
                  onChange={(e) => setChildAge(i, Number(e.target.value))}
                  aria-label={`Child ${i + 1} age`}
                >
                  {Array.from({ length: MAX_AGE - MIN_AGE + 1 }, (_, n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? 'yr' : 'yrs'}
                    </option>
                  ))}
                </select>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

interface StepperProps {
  value: number
  min: number
  max: number
  onChange: (next: number) => void
  ariaLabel: string
}

function Stepper({ value, min, max, onChange, ariaLabel }: StepperProps) {
  return (
    <div className="sc-stepper">
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= min}
        aria-label={`Decrease ${ariaLabel.toLowerCase()}`}
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="sc-stepper-value" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        aria-label={`Increase ${ariaLabel.toLowerCase()}`}
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  )
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

/**
 * Adjust the `childrenAges` array length to match a new child count.
 * Trims when shrinking, pads with the default age when growing. Returns
 * undefined when there are no children (caller deletes the field).
 */
function padOrTrimAges(current: number[] | undefined, nextCount: number): number[] | undefined {
  if (nextCount === 0) return undefined
  const base = current ?? []
  if (base.length === nextCount) return base
  if (base.length > nextCount) return base.slice(0, nextCount)
  return [...base, ...Array.from({ length: nextCount - base.length }, () => DEFAULT_CHILD_AGE)]
}
